import * as React from 'react'

import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Flex, Grid, GridItem, Stack, Text } from '@chakra-ui/react'

import {
  IconButton,
  Link,
  Menu,
  Tag,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowUpRightFromSquareRegular,
  FaEllipsisRegular,
  FaLockOpenRegular,
  FaLockRegular,
  FaShieldKeyholeRegular,
} from '@enechain/ecloud-icons'

import { ProfilePicture } from '~/components/parts/ProfilePicture'
import { Language, Member, MemberStatus } from '~/gen/proto/bff/v1/model_pb'
import { useLoginUser } from '~/hooks/useLoginUser'
import { MemberStatusTag } from '~/pages/Members/components/MembersTable/MemberStatus'
import { dict } from '~/pages/Members/dict'
import { useMemberMutationState } from '~/pages/Members/state/memberMutation'
import { isUserRoleConfigurable } from '~/pages/Members/utils/license'

type Props = {
  member: Member
  onResetMfa: () => void
  onUnlock: () => void
  onSuspend: () => void
  onUnsuspend: () => void
}
const MemberDetailView: React.FC<Props> = ({
  member,
  onResetMfa,
  onUnlock,
  onSuspend,
  onUnsuspend,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useLoginUser()
  const { start: startMemberMutation } = useMemberMutationState(member.id)

  const onClickEdit = (): void => {
    startMemberMutation(member)
    void navigate({
      to: '/members/$memberId/edit',
      params: { memberId: member.id },
    })
  }

  const hasLicense = Object.keys(me.baseLicenseMap).length > 0
  const isLocked = member.isIpLocked || member.isMfaLocked
  const isSuspended = member.status === MemberStatus.SUSPENDED
  const isMyself = member.id === me.id

  return (
    <Stack gap="6" width="100%">
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Flex alignItems="center" gap="6">
          <ProfilePicture src={member.imageUrl} />
          <Flex flexDirection="column">
            <Text textStyle="headerMd">{member.name}</Text>
            <Text>{member.email}</Text>
          </Flex>
        </Flex>
        {hasLicense && (
          <Flex alignItems="center" gap="2">
            <TertiaryButton onClick={onClickEdit}>
              {t('action.edit')}
            </TertiaryButton>
            <Menu
              content={[
                {
                  icon: <FaShieldKeyholeRegular />,
                  label: t('pages.members.action.resetMfa.title'),
                  value: 'resetMfa',
                  onClick: onResetMfa,
                  disabled: !member.mfaEnabled,
                },
                {
                  icon: <FaLockOpenRegular />,
                  label: t('pages.members.action.unlock.title'),
                  value: 'unlock',
                  onClick: onUnlock,
                  disabled: !isLocked,
                },
                {
                  icon: <FaLockRegular />,
                  label: t('pages.members.action.suspend.title'),
                  value: 'suspend',
                  onClick: onSuspend,
                  disabled:
                    isMyself ||
                    isSuspended ||
                    (member.status !== MemberStatus.ACTIVE &&
                      member.status !== MemberStatus.INVITED),
                },
                {
                  icon: <FaLockOpenRegular />,
                  label: t('pages.members.action.unsuspend.title'),
                  value: 'unsuspend',
                  onClick: onUnsuspend,
                  disabled: isMyself || !isSuspended,
                },
              ]}
            >
              <IconButton aria-label="Open Menu">
                <FaEllipsisRegular />
              </IconButton>
            </Menu>
          </Flex>
        )}
      </Flex>

      <Text textStyle="headerMd">{t('pages.members.info')}</Text>
      <Grid
        gridGap="4"
        gridTemplateColumns="320px 1fr"
        gridTemplateAreas={`
          'name vname'
          'englishName venglishName'
          'email vemail'
          'role vrole'
          'language vlanguage'
          'phoneNumber vphoneNumber'
          'mobileNumber vmobileNumber'
          'status vstatus'
          'mfa vmfa'
          'ipLocked vipLocked'
          'mfaLocked vmfaLocked'`}
        width="full"
        marginBottom="2"
        padding="4"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="radiiMd"
      >
        <GridItem gridArea="name" fontWeight="bold">
          {t('pages.members.label.name')}
        </GridItem>
        <GridItem gridArea="vname">{member.name}</GridItem>

        <GridItem gridArea="englishName" fontWeight="bold">
          {t('pages.members.label.englishName')}
        </GridItem>
        <GridItem gridArea="venglishName">{member.englishName}</GridItem>

        <GridItem gridArea="email" fontWeight="bold">
          {t('pages.members.label.email')}
        </GridItem>
        <GridItem gridArea="vemail">{member.email}</GridItem>

        <GridItem gridArea="role" fontWeight="bold">
          {t('pages.members.label.role')}
        </GridItem>
        <GridItem gridArea="vrole">{dict.role(member.role)}</GridItem>

        <GridItem gridArea="language" fontWeight="bold">
          {t('pages.members.label.language')}
        </GridItem>
        <GridItem gridArea="vlanguage">
          {dict.language(member.language)}
        </GridItem>

        <GridItem gridArea="phoneNumber" fontWeight="bold">
          {t('pages.members.label.phoneNumber')}
        </GridItem>
        <GridItem gridArea="vphoneNumber">{member.phoneNumber}</GridItem>

        <GridItem gridArea="mobileNumber" fontWeight="bold">
          {t('pages.members.label.mobileNumber')}
        </GridItem>
        <GridItem gridArea="vmobileNumber">{member.mobileNumber}</GridItem>

        <GridItem gridArea="status" fontWeight="bold">
          {t('pages.members.label.status')}
        </GridItem>
        <GridItem gridArea="vstatus">
          <MemberStatusTag status={member.status} />
        </GridItem>

        <GridItem gridArea="mfa" fontWeight="bold">
          {t('pages.members.label.mfa')}
        </GridItem>
        <GridItem gridArea="vmfa">
          {member.mfaEnabled
            ? t('pages.members.value.enabled')
            : t('pages.members.value.disabled')}
        </GridItem>

        <GridItem gridArea="ipLocked" fontWeight="bold">
          {t('pages.members.label.ipLocked')}
        </GridItem>
        <GridItem gridArea="vipLocked">
          {member.isIpLocked && (
            <Tag
              color="Text.negativeHigh"
              backgroundColor="Surface.negativeMid"
            >
              {t('pages.members.value.locked')}
            </Tag>
          )}
        </GridItem>

        <GridItem gridArea="mfaLocked" fontWeight="bold">
          {t('pages.members.label.mfaLocked')}
        </GridItem>
        <GridItem gridArea="vmfaLocked">
          {member.isMfaLocked && (
            <Tag
              color="Text.negativeHigh"
              backgroundColor="Surface.negativeMid"
            >
              {t('pages.members.value.locked')}
            </Tag>
          )}
        </GridItem>
      </Grid>

      {isUserRoleConfigurable(Object.values(me.baseLicenseMap)) && (
        <Flex flexDirection="column" gap="4">
          <Text textStyle="headerMd">{t('pages.members.roleSettings')}</Text>
          <Grid
            gridGap="4"
            gridTemplateColumns="320px 1fr"
            width="full"
            padding="4"
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="radiiMd"
          >
            {Object.entries(member.userRoleMap?.settingMap ?? {}).map(
              ([key, value]) => {
                const license = member.baseLicenseMap[key]
                if (!license) {
                  return null
                }
                if (
                  license.isUserRoleConfigurable === false ||
                  license.isProductPublic === false
                ) {
                  return null
                }
                return (
                  <React.Fragment key={key}>
                    <GridItem>
                      <Text fontWeight="bold">{`${license.productName} /  ${me.language === Language.ENGLISH ? license.engPlanName : license.planName}`}</Text>
                      <Link
                        href={`/user_roles/${license.planId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Flex alignItems="center" gap="1">
                          {t('pages.members.link.user_role_page')}
                          <FaArrowUpRightFromSquareRegular />
                        </Flex>
                      </Link>
                    </GridItem>
                    <GridItem>
                      {value.settings
                        .filter((s) => s.isSetting)
                        .map((role) =>
                          me.language === Language.ENGLISH
                            ? role.userRoleEngName
                            : role.userRoleName,
                        )
                        .join(', ')}
                    </GridItem>
                  </React.Fragment>
                )
              },
            )}
          </Grid>
        </Flex>
      )}
    </Stack>
  )
}

export default React.memo(MemberDetailView)
